/**
 * The console icon set, drawn to the same rules as the brand mark and to the
 * same rules as namu-app's set: a 1.6 unit hairline on a 24 unit grid, round
 * caps and joins, no fills. The mark is an open arc terminated by a Sahel dot,
 * and every glyph here has to look like it came out of the same pen.
 *
 * Where a glyph already exists in namu-app the path is copied rather than
 * redrawn. A gear that is 2 units wider here than in the phone app is the kind
 * of difference nobody can name and everybody can see, and the two products sit
 * side by side in screenshots.
 *
 * Every icon is presentational. The accessible name always lives on the control
 * that contains it, never on the glyph, which is why `aria-hidden` and
 * `focusable={false}` are baked into `base` rather than left to each caller.
 */

export interface IconProps {
  size?: number
  className?: string
}

/**
 * 18, where the phone app defaults to 22.
 *
 * The console runs its labels at 0.875rem in the nav and 0.8125rem in a table
 * cell. A 22 unit glyph beside 14px text reads as an illustration with a
 * caption rather than as a label with a marker, and the nav row then has to
 * grow to contain it, which costs four rows of navigation on a laptop.
 */
const DEFAULT_SIZE = 18

function base(size: number) {
  return {
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.6,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true,
    focusable: false,
  }
}

/* ============================ Navigation ============================ */

export function DashboardIcon({ size = DEFAULT_SIZE, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <rect x="3.6" y="3.6" width="7" height="7" rx="1.8" />
      <rect x="13.4" y="3.6" width="7" height="7" rx="1.8" />
      <rect x="3.6" y="13.4" width="7" height="7" rx="1.8" />
      <rect x="13.4" y="13.4" width="7" height="7" rx="1.8" />
    </svg>
  )
}

/** Projects: stacked layers, because a project is the thing everything else hangs off. */
export function ProjectsIcon({ size = DEFAULT_SIZE, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M12 3.2 3.5 7.4 12 11.6l8.5-4.2z" />
      <path d="M3.5 12 12 16.2 20.5 12" />
      <path d="M3.5 16.6 12 20.8l8.5-4.2" />
    </svg>
  )
}

/** Knowledge: an open book. What Kura is allowed to say, and nothing else. */
export function KnowledgeIcon({ size = DEFAULT_SIZE, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M12 6.6C10.4 5.2 8.4 4.5 6 4.5H3.6v13H6c2.4 0 4.4.7 6 2.1" />
      <path d="M12 6.6c1.6-1.4 3.6-2.1 6-2.1h2.4v13H18c-2.4 0-4.4.7-6 2.1" />
    </svg>
  )
}

export function ConversationsIcon({ size = DEFAULT_SIZE, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M7.5 13.6H5.4a1.9 1.9 0 0 1-1.9-1.9V5.6a1.9 1.9 0 0 1 1.9-1.9h9.2a1.9 1.9 0 0 1 1.9 1.9v1.2" />
      <rect x="7.4" y="7.6" width="13.1" height="9.3" rx="2.2" />
      <path d="M13 16.9 10.4 20.4v-3.5" />
    </svg>
  )
}

/**
 * Insight, drawn as a waveform rather than as a lightbulb.
 *
 * The lightbulb says somebody had an idea. What this section actually holds is
 * the shape of what a community said out loud: ranked gaps, topics rising and
 * falling, rumours counted. The product's only sensor is a voice, so the glyph
 * is one.
 */
export function InsightsIcon({ size = DEFAULT_SIZE, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M4 10.4v3.2" />
      <path d="M8 6.6v10.8" />
      <path d="M12 3.8v16.4" />
      <path d="M16 7.4v9.2" />
      <path d="M20 10.4v3.2" />
    </svg>
  )
}

/** Cases: the case file. Protection work has called it a file for fifty years. */
export function CasesIcon({ size = DEFAULT_SIZE, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M3.6 7.4V6a1.9 1.9 0 0 1 1.9-1.9h3.3l2 2.4h7.7A1.9 1.9 0 0 1 20.4 8.4v9.6a1.9 1.9 0 0 1-1.9 1.9H5.5a1.9 1.9 0 0 1-1.9-1.9z" />
    </svg>
  )
}

/** Broadcasts: one voice going outwards, symmetric, no megaphone. */
export function BroadcastsIcon({ size = DEFAULT_SIZE, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <circle cx="12" cy="12" r="2.1" />
      <path d="M8.4 8.4a5.1 5.1 0 0 0 0 7.2" />
      <path d="M15.6 15.6a5.1 5.1 0 0 0 0-7.2" />
      <path d="M5.6 5.6a9.1 9.1 0 0 0 0 12.8" />
      <path d="M18.4 18.4a9.1 9.1 0 0 0 0-12.8" />
    </svg>
  )
}

export function TasksIcon({ size = DEFAULT_SIZE, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M4 7.4 6 9.4l3.2-3.6" />
      <path d="M12.6 7.6h7.4" />
      <path d="M4 16.4 6 18.4l3.2-3.6" />
      <path d="M12.6 16.6h7.4" />
    </svg>
  )
}

/** Indicators: a target. What the project is trying to move, not what it did. */
export function IndicatorsIcon({ size = DEFAULT_SIZE, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <circle cx="12" cy="12" r="8.2" />
      <circle cx="12" cy="12" r="3.6" />
      <path d="M12 12h.01" />
    </svg>
  )
}

export function ReportsIcon({ size = DEFAULT_SIZE, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M13.4 3.6H7.2a2 2 0 0 0-2 2v12.8a2 2 0 0 0 2 2h9.6a2 2 0 0 0 2-2V9.2z" />
      <path d="M13.4 3.6v3.8a1.8 1.8 0 0 0 1.8 1.8h3.6" />
      <path d="M8.8 13.6h6.4" />
      <path d="M8.8 16.8h4" />
    </svg>
  )
}

export function TeamIcon({ size = DEFAULT_SIZE, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <circle cx="9.8" cy="8.4" r="3.4" />
      <path d="M3.4 19.4a6.4 6.4 0 0 1 12.8 0" />
      <path d="M16.2 5.4a3.4 3.4 0 0 1 0 6" />
      <path d="M17.6 13.8a6.4 6.4 0 0 1 3 5.6" />
    </svg>
  )
}

/** Copied path-for-path from namu-app's GearIcon. See the file header. */
export function SettingsIcon({ size = DEFAULT_SIZE, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <circle cx="12" cy="12" r="3.2" />
      <path d="M12 2.8v2.4M12 18.8v2.4M21.2 12h-2.4M5.2 12H2.8M18.5 5.5l-1.7 1.7M7.2 16.8l-1.7 1.7M18.5 18.5l-1.7-1.7M7.2 7.2L5.5 5.5" />
    </svg>
  )
}

/* ============================ Table chrome ============================ */

export function SearchIcon({ size = DEFAULT_SIZE, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <circle cx="10.8" cy="10.8" r="6.3" />
      <path d="M15.4 15.4 20.4 20.4" />
    </svg>
  )
}

export function FilterIcon({ size = DEFAULT_SIZE, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M3.8 5.2h16.4l-6.4 7.5v6l-3.6 2v-8z" />
    </svg>
  )
}

/**
 * The neutral sort affordance, for a column that can be sorted but is not.
 * Direction is carried by `aria-sort` and by the arrow icons below, never by
 * this glyph alone.
 */
export function SortIcon({ size = DEFAULT_SIZE, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M7.6 4.6v14.8" />
      <path d="M4.2 8 7.6 4.6 11 8" />
      <path d="M16.4 19.4V4.6" />
      <path d="M13 16l3.4 3.4L19.8 16" />
    </svg>
  )
}

export function ChevronDownIcon({ size = DEFAULT_SIZE, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M5.5 9 12 15.5 18.5 9" />
    </svg>
  )
}

export function ChevronUpIcon({ size = DEFAULT_SIZE, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M5.5 15 12 8.5 18.5 15" />
    </svg>
  )
}

export function ChevronLeftIcon({ size = DEFAULT_SIZE, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M15 5.5 8.5 12 15 18.5" />
    </svg>
  )
}

export function ChevronRightIcon({ size = DEFAULT_SIZE, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M9 5.5 15.5 12 9 18.5" />
    </svg>
  )
}

/** The pair, for a control that opens a list in place: switchers and selects. */
export function ChevronUpDownIcon({ size = DEFAULT_SIZE, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M8 10 12 6l4 4" />
      <path d="M8 14l4 4 4-4" />
    </svg>
  )
}

export function MoreIcon({ size = DEFAULT_SIZE, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M12 6.2h.01M12 12h.01M12 17.8h.01" />
    </svg>
  )
}

/* ============================ Actions ============================ */

export function PlusIcon({ size = DEFAULT_SIZE, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M12 5v14M5 12h14" />
    </svg>
  )
}

/** Copied from namu-app. */
export function CloseIcon({ size = DEFAULT_SIZE, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M5.5 5.5l13 13M18.5 5.5l-13 13" />
    </svg>
  )
}

/** Copied from namu-app. */
export function CheckIcon({ size = DEFAULT_SIZE, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M4.5 12.5l5 5 10-11" />
    </svg>
  )
}

export function DownloadIcon({ size = DEFAULT_SIZE, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M12 3.8v10.8" />
      <path d="M7.8 10.4 12 14.6l4.2-4.2" />
      <path d="M4.4 17.2v1.4a2 2 0 0 0 2 2h11.2a2 2 0 0 0 2-2v-1.4" />
    </svg>
  )
}

/** Leaves the console. Pair it with the link text, never use it alone. */
export function ExternalIcon({ size = DEFAULT_SIZE, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M13.8 4.4h5.8v5.8" />
      <path d="M11.2 12.8 19.6 4.4" />
      <path d="M19 14.4v4.2a2 2 0 0 1-2 2H5.8a2 2 0 0 1-2-2V7.4a2 2 0 0 1 2-2H10" />
    </svg>
  )
}

/** Copied from namu-app. */
export function SendIcon({ size = DEFAULT_SIZE, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M4 12h14" />
      <path d="M13 6.5l5.5 5.5-5.5 5.5" />
    </svg>
  )
}

/** Copied from namu-app. Playing back a conversation, not starting one. */
export function PlayIcon({ size = DEFAULT_SIZE, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M8 5l11 7-11 7z" />
    </svg>
  )
}

/* ============================ Status and meaning ============================ */

export function AlertIcon({ size = DEFAULT_SIZE, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M10.6 4.6 2.9 18.3a1.6 1.6 0 0 0 1.4 2.4h15.4a1.6 1.6 0 0 0 1.4-2.4L13.4 4.6a1.6 1.6 0 0 0-2.8 0z" />
      <path d="M12 9.6v4.2" />
      <path d="M12 17.4h.01" />
    </svg>
  )
}

export function InfoIcon({ size = DEFAULT_SIZE, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <circle cx="12" cy="12" r="8.4" />
      <path d="M12 11.2v5" />
      <path d="M12 7.9h.01" />
    </svg>
  )
}

/**
 * A knowledge gap: a question the published knowledge could not answer.
 *
 * A bare question mark rather than one inside a circle, because InfoIcon is
 * already a circle with a stem and a dot, and at 18 pixels the two are the same
 * silhouette. These two appear in the same insight views.
 */
export function GapIcon({ size = DEFAULT_SIZE, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M8.8 8.8a3.2 3.2 0 1 1 4.5 2.9c-.9.4-1.4 1.2-1.4 2.1v.7" />
      <path d="M11.9 18.4h.01" />
    </svg>
  )
}

/**
 * Direction of travel. Direction is also stated in words wherever these appear:
 * an arrow is a shape, but "up" is only good news for an indicator whose
 * `direction` is `higher_is_better`, and the glyph cannot know that.
 */
export function TrendUpIcon({ size = DEFAULT_SIZE, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M3.6 16.8 9.4 11l3.6 3.6 7.4-7.4" />
      <path d="M14.8 7.2h5.6v5.6" />
    </svg>
  )
}

export function TrendDownIcon({ size = DEFAULT_SIZE, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M3.6 7.2 9.4 13l3.6-3.6 7.4 7.4" />
      <path d="M14.8 16.8h5.6v-5.6" />
    </svg>
  )
}

/* ============================ Records ============================ */

/** Copied from namu-app's PersonIcon. */
export function UserIcon({ size = DEFAULT_SIZE, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <circle cx="12" cy="8.5" r="3.6" />
      <path d="M4.8 20a7.2 7.2 0 0 1 14.4 0" />
    </svg>
  )
}

export function CalendarIcon({ size = DEFAULT_SIZE, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <rect x="3.6" y="5.4" width="16.8" height="15" rx="2.2" />
      <path d="M8 3.4v4M16 3.4v4" />
      <path d="M3.6 10.4h16.8" />
    </svg>
  )
}

export function ClockIcon({ size = DEFAULT_SIZE, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <circle cx="12" cy="12" r="8.4" />
      <path d="M12 7.2V12l3.2 1.9" />
    </svg>
  )
}

/**
 * A place, and only ever a place a piece of work was filed from.
 *
 * The platform deliberately does not model continuous location tracking of
 * field staff, so this icon has no live variant and must not acquire one.
 */
export function LocationIcon({ size = DEFAULT_SIZE, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M12 20.8s6.6-5.4 6.6-10.2a6.6 6.6 0 1 0-13.2 0c0 4.8 6.6 10.2 6.6 10.2z" />
      <circle cx="12" cy="10.6" r="2.5" />
    </svg>
  )
}

/* ============================ Credentials ============================ */

/**
 * An eye, and the same eye struck through, for a password reveal control.
 *
 * Two exports rather than one with a `revealed` prop, because the icon set's
 * rule is that a glyph is a drawing and takes no state. The strike is drawn as
 * a second path over the identical eye, so the two are the same silhouette with
 * one mark added: the stroke reads as a negation rather than as a different
 * picture, and at 18 pixels a genuinely different drawing does not.
 *
 * The reveal control's accessible name does not change with its state, which is
 * what these two are here to allow. The state travels on `aria-pressed`, and
 * the strike is the visible second channel beside it, because a control whose
 * only difference between states is a pressed background is one most people
 * cannot read at a glance.
 */
export function EyeIcon({ size = DEFAULT_SIZE, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  )
}

export function EyeOffIcon({ size = DEFAULT_SIZE, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12Z" />
      <circle cx="12" cy="12" r="3" />
      <path d="M4 20 20 4" />
    </svg>
  )
}

/* ============================ Shell chrome ============================ */

/** Opens the navigation drawer on a tablet. Not shown at desk widths. */
export function MenuIcon({ size = DEFAULT_SIZE, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M4 7h16M4 12h16M4 17h16" />
    </svg>
  )
}

/** Collapses and expands the sidebar. One glyph for both, because the control
 *  is one button whose name never changes and whose state is `aria-expanded`. */
export function PanelLeftIcon({ size = DEFAULT_SIZE, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <rect x="3.4" y="4.6" width="17.2" height="14.8" rx="2.2" />
      <path d="M9.6 4.6v14.8" />
    </svg>
  )
}

export function SunIcon({ size = DEFAULT_SIZE, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2.8v2.2M12 19v2.2M21.2 12H19M5 12H2.8M18.5 5.5l-1.6 1.6M7.1 16.9l-1.6 1.6M18.5 18.5l-1.6-1.6M7.1 7.1 5.5 5.5" />
    </svg>
  )
}

export function MoonIcon({ size = DEFAULT_SIZE, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M20.4 13.6A8.4 8.4 0 0 1 10.4 3.6a8.4 8.4 0 1 0 10 10z" />
    </svg>
  )
}

/** "Match the operating system", for the third state of the scheme control. */
export function SystemIcon({ size = DEFAULT_SIZE, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <rect x="3.2" y="4.6" width="17.6" height="12" rx="2.2" />
      <path d="M12 16.6v3.8" />
      <path d="M8.4 20.4h7.2" />
    </svg>
  )
}

/** Row density. Uneven gaps, because even ones read as a list rather than a scale. */
export function DensityIcon({ size = DEFAULT_SIZE, className }: IconProps) {
  return (
    <svg {...base(size)} className={className}>
      <path d="M4 5h16M4 9.4h16M4 13.8h16M4 19.4h16" />
    </svg>
  )
}

/* ============================ Brand ============================ */

/**
 * The Blossom mark, static. Copied from namu-app.
 *
 * Used where the full wordmark does not fit, which in this application means
 * the collapsed sidebar. Drawn rather than loaded from `/brand/namu-icon.svg`
 * so the arc follows `currentColor` through a scheme change with no second
 * request and no second file to keep in step.
 */
export function BlossomMark({ size = 22, className }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 120 120"
      fill="none"
      aria-hidden
      focusable={false}
      className={className}
    >
      <path
        d="M 104 60 A 44 44 0 1 0 60 104"
        stroke="currentColor"
        strokeWidth="6"
        strokeLinecap="round"
      />
      <circle cx="60" cy="104" r="9" fill="var(--brand-sahel)" />
    </svg>
  )
}
